import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { LoadingButton } from '@mui/lab';
import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Box, Button } from '@mui/material';
import { GeneralSelectMenu } from 'src/components/general';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { schema } from './validationSchema';
import { getDefaultValues } from './defaultValues';
import { generateSelectMenuItems } from 'src/utils';
import {
  networkCreateGatewayJoinProfile,
  networkUpdateGatewayJoinProfile,
  useFetchGatewayJoinProfileActiveTypeList,
} from 'src/network/gateway-join-profile';
import { useFetchGatewayProfileList } from 'src/network/gateway-profile';
import { useFetchGatewayList } from 'src/network/gateway';
import { GATEWAY_JOIN_PROFILE_LIST } from 'src/constants/queryKeys';
import useFormsModalStore from 'src/store/forms';

const GatewayJoinProfileForm = () => {
  const { modal, closeModal } = useFormsModalStore();

  const queryClient = useQueryClient();

  const { enqueueSnackbar } = useSnackbar();

  const [gatewayList, setGatewayList] = useState([]);
  const [selectedGateway, setSelectedGateway] = useState('');

  const [gatewayProfileList, setGatewayProfileList] = useState([]);
  const [selectedGatewayProfile, setSelectedGatewayProfile] = useState('');

  const [gatewayProfileActiveTypes, setGatewayProfileActiveTypes] = useState([]);
  const [selectedGatewayProfileActiveType, setSelectedGatewayProfileActiveType] = useState('');

  const [filterItems, setFilterItems] = useState({
    ID: -1,
  });

  const gatewayJoinProfileDetails = modal?.data;
  const isEditMode = !!gatewayJoinProfileDetails;

  const defaultValues = getDefaultValues(gatewayJoinProfileDetails);

  const methods = useForm({
    // resolver: yupResolver(schema), // todo schema
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      GatewayJoinGatewayProfileGatewayID: selectedGateway,
      GatewayJoinGatewayProfileGatewayProfileID: selectedGatewayProfile,
      GatewayJoinGatewayProfileGatewayJoinGatewayProfileActiveTypeID: selectedGatewayProfileActiveType,
      GatewayJoinGatewayProfileMinAmout: +values?.GatewayJoinGatewayProfileMinAmout || 0,
      GatewayJoinGatewayProfileMaxAmount: +values?.GatewayJoinGatewayProfileMaxAmount || 0,
      GatewayJoinGatewayProfileActiveDays: +values?.GatewayJoinGatewayProfileActiveDays || 0,
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateGatewayJoinProfile(gatewayJoinProfileDetails?.GatewayJoinGatewayProfileID, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('آیتم موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
          }
          await queryClient.invalidateQueries({ queryKey: [GATEWAY_JOIN_PROFILE_LIST] });
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      } else {
        const res = await networkCreateGatewayJoinProfile(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('آیتم موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          reset();
          await queryClient.invalidateQueries({ queryKey: [GATEWAY_JOIN_PROFILE_LIST] });
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  // -----------------------------------------------------------------------

  const { data: activeTypesResult } = useFetchGatewayJoinProfileActiveTypeList();
  const { data: gatewayListResult } = useFetchGatewayList(filterItems, 0);
  const { data: gatewayProfileListResult } = useFetchGatewayProfileList(filterItems, 0);

  useEffect(() => {
    if (activeTypesResult) {
      const result = generateSelectMenuItems(
        activeTypesResult?.data?.items,
        'GatewayJoinGatewayProfileActiveTypeLabel',
        'GatewayJoinGatewayProfileActiveTypeID'
      );

      setGatewayProfileActiveTypes(result);
      setSelectedGatewayProfileActiveType(
        gatewayJoinProfileDetails?.GatewayJoinGatewayProfileGatewayJoinGatewayProfileActiveTypeID || result?.[0]?.id
      );
    }
  }, [activeTypesResult]);

  useEffect(() => {
    if (gatewayListResult) {
      const result =
        gatewayListResult?.data?.items?.map((item) => {
          return {
            id: item?.GatewayID,
            title: item?.GatewayLabel,
          };
        }) || [];

      setGatewayList(result);
      setSelectedGateway(gatewayJoinProfileDetails?.GatewayJoinGatewayProfileGatewayID || result?.[0]?.id);
    }
  }, [gatewayListResult]);

  useEffect(() => {
    if (gatewayProfileListResult) {
      const result =
        gatewayProfileListResult?.data?.items?.map((item) => {
          return {
            id: item?.GatewayProfileID,
            title: item?.GatewayProfileLabel,
          };
        }) || [];

      setGatewayProfileList(result);
      setSelectedGatewayProfile(gatewayJoinProfileDetails?.GatewayJoinGatewayProfileGatewayProfileID || result?.[0]?.id);
    }
  }, [gatewayProfileListResult]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField name="GatewayJoinGatewayProfileAccessToken" type="text" label="توکن" />

              <GeneralSelectMenu
                label="نام درگاه پرداخت"
                name="GatewayJoinGatewayProfileGatewayID"
                options={gatewayList}
                value={selectedGateway}
                onChangeValue={setSelectedGateway}
              />

              <GeneralSelectMenu
                label="پروفایل محصول"
                name="GatewayJoinGatewayProfileGatewayProfileID"
                options={gatewayProfileList}
                value={selectedGatewayProfile}
                onChangeValue={setSelectedGatewayProfile}
              />

              <GeneralSelectMenu
                label="وضعیت"
                name="GatewayJoinGatewayProfileGatewayJoinGatewayProfileActiveTypeID"
                options={gatewayProfileActiveTypes}
                value={selectedGatewayProfileActiveType}
                onChangeValue={setSelectedGatewayProfileActiveType}
              />

              <RHFTextField name="GatewayJoinGatewayProfileMinAmout" type="text" label="حداقل خرید" />

              <RHFTextField name="GatewayJoinGatewayProfileMaxAmount" type="text" label="حداکثر خرید" />

              <RHFTextField name="GatewayJoinGatewayProfileActiveDays" type="text" label="تعداد روزهای قعال" />
            </Box>

            <div className="flex items-center gap-4 mt-6">
              <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                ذخیره
              </LoadingButton>
              <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
              </LoadingButton>
              <Button onClick={closeModal} variant="contained" color="error">
                انصراف
              </Button>
            </div>
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default GatewayJoinProfileForm;
